import { useState, useEffect, useMemo, useCallback } from 'react'
import { Button, Input, Select, Table, Tabs, Typography } from 'antd'
import type { TableRowSelection } from 'antd/es/table/interface'
import ModelDialog from '@/components/ModelDialog'
import { getResearchPoolStocks, getStocks } from '@/api'
import type { BatchAddStockItem, ResearchPoolStock, Stock } from '@/types'

const { Text } = Typography

const RESEARCH_PAGE_SIZE = 50
const MARKET_OPTIONS = [
    { label: '全部市场', value: '' },
    { label: '港股 HK', value: 'HK' },
    { label: '美股 US', value: 'US' },
    { label: '沪市 SH', value: 'SH' },
    { label: '深市 SZ', value: 'SZ' },
]

export interface StockOption {
    id: number
    name: string
    code: string
    industry: string
    market?: string
    sc_name?: string
    tc_name?: string
}

interface AddStockDialogProps {
    open: boolean
    poolId: number
    onCancel: () => void
    onAdd: (data: Stock) => void
    onBatchAdd: (stocks: BatchAddStockItem[]) => void | Promise<void>
    title?: string
    items?: StockOption[]
}

function stockDisplayName(stock: Pick<Stock, 'name' | 'market' | 'sc_name' | 'tc_name'>) {
    if (stock.market === 'CN') return stock.sc_name || stock.name
    if (stock.market === 'HK') return stock.tc_name || stock.name
    return stock.name
}

function formatMarketCap(value?: number | null) {
    if (value == null || Number.isNaN(Number(value))) return '--'
    const n = Number(value)
    if (n >= 1e12) return `${(n / 1e12).toFixed(2)}T`
    if (n >= 1e9) return `${(n / 1e9).toFixed(2)}B`
    if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M`
    return n.toLocaleString()
}

export default function AddStockDialog({
    open,
    poolId: _poolId,
    onCancel,
    onAdd,
    onBatchAdd,
    items = [] as StockOption[],
}: AddStockDialogProps) {
    const [activeTab, setActiveTab] = useState('search')
    const [keyword, setKeyword] = useState('')
    const [stocks, setStocks] = useState<Stock[]>([])

    const [researchMarket, setResearchMarket] = useState('')
    const [researchKeyword, setResearchKeyword] = useState('')
    const [researchQuery, setResearchQuery] = useState('')
    const [researchItems, setResearchItems] = useState<ResearchPoolStock[]>([])
    const [researchTotal, setResearchTotal] = useState(0)
    const [researchPage, setResearchPage] = useState(1)
    const [researchLoading, setResearchLoading] = useState(false)
    const [selectedCodes, setSelectedCodes] = useState<string[]>([])
    const [batchSubmitting, setBatchSubmitting] = useState(false)

    const existingCodes = useMemo(
        () => new Set(items.map((item) => String(item.code || '').toUpperCase())),
        [items],
    )

    const isInPool = useCallback(
        (code: string) => existingCodes.has(String(code || '').toUpperCase()),
        [existingCodes],
    )

    useEffect(() => {
        if (!open) {
            setActiveTab('search')
            setKeyword('')
            setStocks([])
            setResearchMarket('')
            setResearchKeyword('')
            setResearchQuery('')
            setResearchItems([])
            setResearchTotal(0)
            setResearchPage(1)
            setSelectedCodes([])
            setBatchSubmitting(false)
        }
    }, [open])

    useEffect(() => {
        if (!open || activeTab !== 'search') return
        const q = keyword.trim()
        if (!q) {
            setStocks([])
            return
        }
        let cancelled = false
        getStocks(q)
            .then((res) => {
                if (!cancelled) setStocks(Array.isArray(res) ? res : [])
            })
            .catch(() => {
                if (!cancelled) setStocks([])
            })
        return () => {
            cancelled = true
        }
    }, [keyword, open, activeTab])

    useEffect(() => {
        if (!open || activeTab !== 'research') return
        let cancelled = false
        setResearchLoading(true)
        const q = researchQuery.trim()
        const params: Parameters<typeof getResearchPoolStocks>[0] = {
            market: researchMarket || undefined,
            limit: RESEARCH_PAGE_SIZE,
            offset: (researchPage - 1) * RESEARCH_PAGE_SIZE,
            sort_by: 'total_market_val',
            sort_order: 'desc',
        }
        if (q) {
            if (q.includes('.')) params.stock_code = q
            else params.stock_name = q
        }
        getResearchPoolStocks(params)
            .then((res) => {
                if (cancelled) return
                setResearchItems(res.items)
                setResearchTotal(res.total)
                setSelectedCodes((prev) =>
                    prev.filter((code) =>
                        res.items.some(
                            (item) =>
                                item.stock_code === code && !isInPool(item.stock_code),
                        ),
                    ),
                )
            })
            .catch(() => {
                if (cancelled) return
                setResearchItems([])
                setResearchTotal(0)
            })
            .finally(() => {
                if (!cancelled) setResearchLoading(false)
            })
        return () => {
            cancelled = true
        }
    }, [open, activeTab, researchMarket, researchQuery, researchPage, isInPool])

    const selectableResearchCodes = useMemo(
        () => researchItems.filter((item) => !isInPool(item.stock_code)).map((item) => item.stock_code),
        [researchItems, isInPool],
    )

    const rowSelection: TableRowSelection<ResearchPoolStock> = {
        selectedRowKeys: selectedCodes,
        onChange: (keys) => setSelectedCodes(keys.map(String)),
        getCheckboxProps: (record) => ({
            disabled: isInPool(record.stock_code),
        }),
    }

    const handleBatchImport = async () => {
        const stocksToAdd = researchItems
            .filter((item) => selectedCodes.includes(item.stock_code) && !isInPool(item.stock_code))
            .map((item) => ({
                code: item.stock_code,
                name: item.stock_name || item.stock_code,
            }))
        if (!stocksToAdd.length) return
        setBatchSubmitting(true)
        try {
            await onBatchAdd(stocksToAdd)
        } finally {
            setBatchSubmitting(false)
        }
    }

    const searchTab = (
        <div className='add-stock-dialog-content'>
            <div style={{ marginBottom: 16 }}>
                <Text className='text-12 color-2'>搜索个股</Text>
                <Input
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    placeholder="输入股票名称或代码..."
                    allowClear
                    size="large"
                    styles={{
                        root: { marginTop: 8 },
                        input: { color: '#fff', fontSize: 12 },
                    }}
                />
            </div>
            <div
                style={{
                    maxHeight: 280,
                    overflowY: 'auto',
                    marginBottom: 16,
                    padding: '0 12px',
                }}
            >
                {stocks.map((item) => (
                    <div
                        key={item.code}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 12,
                            padding: '14px 0',
                            borderBlockEnd: '1px solid rgba(255,255,255,0.08)',
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                            <Text style={{ color: '#fff', fontSize: 13 }}>
                                {stockDisplayName(item)}
                            </Text>
                            <Text style={{ color: 'var(--text-2)', fontSize: 12 }}>
                                {item.code}
                            </Text>
                            {item.industry ? (
                                <Text style={{ color: 'var(--text-3)', fontSize: 12 }}>
                                    {item.industry}
                                </Text>
                            ) : null}
                        </div>
                        <Button
                            type="primary"
                            size="small"
                            disabled={isInPool(item.code)}
                            onClick={() => onAdd({
                                ...item,
                                name: stockDisplayName(item) || item.name || item.code,
                            })}
                        >
                            {isInPool(item.code) ? '已添加' : '添加'}
                        </Button>
                    </div>
                ))}
            </div>
        </div>
    )

    const researchTab = (
        <div className='add-stock-dialog-content'>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                <Select
                    value={researchMarket}
                    options={MARKET_OPTIONS}
                    onChange={(value) => {
                        setResearchMarket(value)
                        setResearchPage(1)
                        setSelectedCodes([])
                    }}
                    style={{ width: 120 }}
                />
                <Input
                    value={researchKeyword}
                    onChange={(e) => setResearchKeyword(e.target.value)}
                    onPressEnter={() => {
                        setResearchQuery(researchKeyword.trim())
                        setResearchPage(1)
                        setSelectedCodes([])
                    }}
                    placeholder="名称或代码（如 AAPL / US.AAPL）"
                    allowClear
                    style={{ flex: 1 }}
                />
                <Button
                    type="default"
                    onClick={() => {
                        setResearchQuery(researchKeyword.trim())
                        setResearchPage(1)
                        setSelectedCodes([])
                    }}
                >
                    查询
                </Button>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <Text style={{ color: 'var(--text-3)', fontSize: 12 }}>
                    已选 {selectedCodes.length} / 本页可选 {selectableResearchCodes.length}
                </Text>
                <Button
                    type="link"
                    size="small"
                    disabled={!selectableResearchCodes.length}
                    onClick={() => setSelectedCodes(selectableResearchCodes)}
                >
                    全选本页
                </Button>
            </div>
            <Table<ResearchPoolStock>
                size="small"
                rowKey="stock_code"
                loading={researchLoading}
                dataSource={researchItems}
                rowSelection={rowSelection}
                pagination={{
                    current: researchPage,
                    pageSize: RESEARCH_PAGE_SIZE,
                    total: researchTotal,
                    showSizeChanger: false,
                    onChange: (page) => {
                        setResearchPage(page)
                        setSelectedCodes([])
                    },
                }}
                scroll={{ y: 280 }}
                columns={[
                    {
                        title: '名称',
                        dataIndex: 'stock_name',
                        ellipsis: true,
                        render: (value: string, row) => (
                            <span>
                                {value || '--'}
                                {isInPool(row.stock_code) ? (
                                    <Text style={{ color: 'var(--text-3)', marginLeft: 6, fontSize: 11 }}>
                                        已在池中
                                    </Text>
                                ) : null}
                            </span>
                        ),
                    },
                    { title: '代码', dataIndex: 'stock_code', width: 110 },
                    { title: '市场', dataIndex: 'market', width: 64 },
                    {
                        title: '市值',
                        dataIndex: 'total_market_val',
                        width: 88,
                        render: (v: number | null) => formatMarketCap(v),
                    },
                    {
                        title: '行业',
                        dataIndex: 'gics_sector',
                        ellipsis: true,
                        render: (v: string | null) => v || '--',
                    },
                ]}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                <Button
                    type="primary"
                    loading={batchSubmitting}
                    disabled={!selectedCodes.length}
                    onClick={handleBatchImport}
                >
                    批量加入（{selectedCodes.length}）
                </Button>
            </div>
        </div>
    )

    return (
        <ModelDialog
            open={open}
            title="添加个股"
            onCancel={onCancel}
            closable={false}
            width={activeTab === 'research' ? 720 : 460}
        >
            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={[
                    { key: 'search', label: '搜索添加', children: searchTab },
                    { key: 'research', label: '从研究池导入', children: researchTab },
                ]}
            />
        </ModelDialog>
    )
}
